<template>
    <teleport to="#Dialogs">
        <Transition :name="allowTransition ? 'Dialog' : undefined" :duration="{ enter: 600, leave: 400 }">
            <div v-if="isOpened" :id="id" class="Dialog" :class="$attrs.class" tabindex="-1" aria-modal="true" aria-label="Modal window">
                <div class="Dialog-wrap" @click="backdropClose && close()">
                    <div class="Dialog-content" :class="{ 'full-screen': fullScreen }" @click="backdropClose && ( $event.stopPropagation() )">
                        <div v-if="$slots.header || title || subtitle" class="Dialog-content-header">
                            <slot name="header">
                                <div v-if="title" class="Dialog-content-header-title">{{title}}</div>
                                <div v-if="subtitle" class="Dialog-content-header-subltitle">{{subtitle}}</div>
                            </slot>
                        </div>

                        <div class="Dialog-content-body">
                            <slot v-if="$slots.body" name="body"></slot>
                            <slot v-else></slot>
                        </div>

                        <div v-if="$slots.footer" class="Dialog-content-footer">
                            <slot name="footer"></slot>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </teleport>
</template>
<script setup>
import { ref, getCurrentInstance, onBeforeMount } from "vue";

defineOptions({ inheritAttrs: false });

const app = getCurrentInstance();

const { $dialogs, $dynamicComponent } = app.appContext.config.globalProperties;

const props = defineProps(
{
    id              : { type: String, required: true },
    title           : { type: String, required: false },
    subtitle        : { type: String, required: false },
    fullScreen      : { type: Boolean, default: false },
    allowTransition : { type: Boolean, default: true },
    backdropClose   : { type: Boolean, default: true },
})

const emit = defineEmits(['on:open','on:close']);

const isOpened = ref();

const open = ( ctx = {} ) =>
{
    $dialogs[ props.id ].ctx = ctx;

    isOpened.value = true;

    emit('on:open');
}

const close = ( data ) =>
{
    delete $dialogs[props.id].ctx;

    isOpened.value = false;

    ( $dynamicComponent && $dynamicComponent.destroy( props.id ) );

    emit('on:close', data );
}

$dialogs[props.id] = { open, close };

onBeforeMount( () =>
{
    if( document.querySelector( '#Dialogs' ) === null )
    {
        const dialogs    = document.createElement('div');
              dialogs.id = 'Dialogs';

        document.body.appendChild( dialogs );
    }
});
</script>

<style lang="scss">
body
{
    &[data-theme="light"]
    {
        .Dialog
        {
            --dialog-background-color: rgba(0, 0, 0, 0.5);
        }
    }

    &[data-theme="dark"]
    {
        .Dialog
        {
            --dialog-background-color: rgba(176, 178, 191, 0.2);
        }
    }
}

.Dialog-enter-active
{
    transition-property: visibility, opacity;
    transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
    transition-duration: 300ms;

    .Dialog-content
    {
        transition-property: opacity, transform;
        transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
        transition-duration: 300ms;
        transition-delay: 250ms;
    }
}

.Dialog-leave-active
{
    transition-property: visibility, opacity;
    transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
    transition-duration: 200ms;
    transition-delay: 150ms;

    .Dialog-content
    {
        transition-property: opacity, transform;
        transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
        transition-duration: 200ms;
    }
}

.Dialog-enter-from,
.Dialog-leave-to
{
    opacity: 0;
    visibility: hidden;

    .Dialog-content
    {
        opacity: 0;
        transform: translateY(#{px(4)});
    }
}

.Dialog
{
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 999;

    background-color: var(--dialog-background-color);

    overflow-y: auto;
    overscroll-behavior: contain;

    -webkit-overflow-scrolling: touch;

    .Dialog-wrap
    {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        width: 100%;
        min-height: 100%;

        padding: px(48);

        .Dialog-content
        {
            display: flex;
            flex-direction: column;

            min-width: px(256);
            max-width: px(512);

            border-radius: 4px;

            background: var(--layout-secondary-background-color);
            box-shadow: var(--elevation-xl-shadow);

            overflow: hidden;

            &.full-screen
            {
                flex-grow: 1;
                min-width: 100%;
                max-width: 100%;
                min-height: 100%;
            }

            .Dialog-content-header
            {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: px(4);

                padding: px(16) px(24);

                border-bottom: 1px solid var(--layout-border-color);

                .Dialog-content-header-title
                {
                    @include font-title-md-semibold;

                    color: var(--primary-text-color);
                }

                .Dialog-content-header-subltitle
                {
                    @include font-body-sm-regular;

                    color: var(--secondary-text-color);
                }
            }

            .Dialog-content-body
            {
                @include font-title-xs-regular;

                flex-grow: 1;

                padding: px(24);

                color: var(--primary-text-color);
            }

            .Dialog-content-footer
            {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: px(12);

                padding: px(24);

                border-top: 1px solid var(--layout-border-color);
            }
        }
    }
}
</style>
